import {
  Button,
  FormField,
  Input,
  Popover,
  SpaceBetween,
} from '@cloudscape-design/components';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import parseScanCode, { ScanTypes } from '../utils/parseScanCode';
import CameraScanner from './CameraScanner';

/**
 * Unified scan field: a text input plus a camera scanner, both emitting
 * parsed codes through onScan({ type, id }). `accept` limits which code
 * types the field takes (travelers, lots, or both — the default); scans
 * of another type are rejected inline.
 *
 * The input accepts a handheld (keyboard-wedge) barcode scanner — which types
 * the QR payload and sends Enter — as well as manual typing/pasting of a
 * traveler ID or URL. Enter parses the value, reports errors inline, and
 * clears the field so back-to-back scans just work. Focus is restored after
 * each successful scan to keep the field hot for the next one.
 *
 * cameraMode='popover' puts the camera behind a button next to the input;
 * 'inline' renders it always-on below the input (for dedicated scan modals).
 *
 * Callbacks are kept in refs so the camera scanner's useEffect never re-fires
 * due to changed function identity — prevents camera restart flash.
 *
 * closeOnScan (popover mode only): Cloudscape's Popover is uncontrolled, so
 * closing works by remounting it via a key bump, which also stops the camera.
 * Leave false for scan-many flows.
 *
 * allowGenerate adds a "Generate ID" button that mints a fresh UUID and emits
 * it through the same onScan path as scans — for travelers that don't have a
 * printed label yet.
 */
export default function ScanField({
  id,
  onScan,
  accept = Object.values(ScanTypes),
  cameraMode = 'popover',
  autoFocus = false,
  label = null,
  description = null,
  errorText = null,
  placeholder = null,
  buttonText = null,
  popoverPosition = 'bottom',
  closeOnScan = false,
  allowGenerate = false,
}) {
  const [value, setValue] = useState('');
  const [inputError, setInputError] = useState(null);
  const inputRef = useRef(null);

  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const closeOnScanRef = useRef(closeOnScan);
  closeOnScanRef.current = closeOnScan;

  const [popoverKey, setPopoverKey] = useState(0);

  // Focus the input on mount if autoFocus is true. Use requestAnimationFrame to
  // avoid a React warning about focus being called during render.
  useEffect(() => {
    if (!autoFocus) return undefined;
    const raf = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(raf);
  }, [autoFocus]);

  const handleEnter = useCallback(() => {
    setInputError(null);
    if (!value.trim()) return;
    const { type, id: scannedId, error } = parseScanCode(value);
    if (error) {
      setInputError(error);
      return;
    }
    if (!accept.includes(type)) {
      setInputError(`Scanned a ${type} code — this field accepts ${accept.join(' or ')} codes.`);
      return;
    }
    setValue('');
    onScanRef.current({ type, id: scannedId });
    // Keep the field hot for rapid back-to-back handheld scans.
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [value, accept]);

  // Stable identity so the camera scanner's useEffect never re-fires.
  const handleCameraScan = useCallback((scan) => {
    setInputError(null);
    onScanRef.current(scan);
    if (closeOnScanRef.current) setPopoverKey((key) => key + 1);
    // Hand focus (back) to the input so a handheld scanner works right away.
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const handleGenerate = useCallback(() => {
    setInputError(null);
    onScanRef.current({ type: ScanTypes.TRAVELER, id: uuidv4() });
    // Hand focus (back) to the input so a handheld scanner works right away.
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  // Wording tracks what the field accepts ("traveler", "lot", "traveler or lot").
  const acceptLabel = accept.join(' or ');
  const fieldPlaceholder = placeholder ?? `Scan or type a ${acceptLabel} ID…`;
  const scanButtonText = buttonText ?? `Scan ${acceptLabel}`;
  // Callers pass buttonText="" for an icon-only trigger — the popover
  // still needs a real header, so fall back past the empty string.
  const popoverHeader = scanButtonText || `Scan ${acceptLabel}`;

  // Generated IDs are traveler UUIDs — hide the button when travelers aren't accepted.
  const generateButton = allowGenerate && accept.includes(ScanTypes.TRAVELER) && (
    <Button iconName="add-plus" formAction="none" onClick={handleGenerate}>
      Generate ID
    </Button>
  );

  const input = (
    <Input
      ref={inputRef}
      autoFocus={autoFocus}
      value={value}
      placeholder={fieldPlaceholder}
      onChange={({ detail }) => setValue(detail.value)}
      onKeyDown={({ detail }) => {
        if (detail.key === 'Enter') handleEnter();
      }}
    />
  );

  return (
    <FormField
      label={label}
      description={description}
      errorText={inputError || errorText}
      stretch
    >
      {cameraMode === 'inline' ? (
        <SpaceBetween size="s">
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ flexGrow: 1 }}>
              {input}
            </div>
            {generateButton}
          </div>
          <CameraScanner
            id={id}
            accept={accept}
            onScan={handleCameraScan}
          />
        </SpaceBetween>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ flexGrow: 1 }}>
            {input}
          </div>
          <Popover
            key={popoverKey}
            position={popoverPosition}
            size="large"
            triggerType="custom"
            header={popoverHeader}
            fixedWidth
            content={(
              <CameraScanner
                id={id}
                accept={accept}
                onScan={handleCameraScan}
              />
            )}
          >
            <Button iconName="search" formAction="none" ariaLabel={`${popoverHeader} with camera`}>
              {scanButtonText}
            </Button>
          </Popover>
          {generateButton}
        </div>
      )}
    </FormField>
  );
}

ScanField.propTypes = {
  id: PropTypes.string.isRequired,
  onScan: PropTypes.func.isRequired,
  accept: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ScanTypes))),
  cameraMode: PropTypes.oneOf(['popover', 'inline']),
  autoFocus: PropTypes.bool,
  label: PropTypes.node,
  description: PropTypes.node,
  errorText: PropTypes.node,
  placeholder: PropTypes.string,
  buttonText: PropTypes.string,
  popoverPosition: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  closeOnScan: PropTypes.bool,
  allowGenerate: PropTypes.bool,
};
